// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Icons
import { faShieldCheck } from "@fortawesome/pro-solid-svg-icons";

// Types
import { IAdilarTableData, RiskScoreDto } from "../../../../types/DueDiligenceTypes";

// Utils
import { formatDateToIcelandic, getTableDate, insertDash } from "../../../../services/utils";

// UI Elements
import Button from "../../../../ui-elements/Button/Button";
import DisplayBox from "../../../../ui-elements/DisplayBox/DisplayBox";
import Label from "../../../../ui-elements/Label/Label";
import Table from "../../../../ui-elements/Table/Table";
import Tooltip from "../../../../ui-elements/Tooltip/Tooltip";
import Loading from "../../../../ui-elements/Loading/Loading";
import { AlertType } from "../../../../ui-elements/Alert/Alert";
import Bugsnag from "@bugsnag/js";
import { useState } from "react";
import { PepResult } from "../../../../types/PepTypes";
import { IPepArray } from "../../../PEP/PepPage";
import { sortEntries } from "../../../PEP/DomesticSearch";
import { AlthjodlegirValistarTooltipColumnHeader, AthaettusomRikiTooltipColumnHeader, RenderNationalityWithTooltip, StjornmalalegTengslPEPTooltipColumnHeader } from "../Constants";

interface IProps {
    customerId?: number;
    adilarTableData: IAdilarTableData[];
    riskScore: RiskScoreDto;
    setEditContactModal_isOpen: (isOpen: boolean) => void;
    setEditContactModal_contacts: (contacts: any) => void;
    setForeignPepModal_isOpen(show: boolean): void;
    setForeignPepModal_data(data: PepResult[] | undefined): void;
    setPepModal_isOpen(show: boolean): void;
    setPepModal_data(data: IPepArray[] | undefined): void;
    setToastShow(show: boolean): void;
    setToastType(type: AlertType): void;
    setToastMessage(message: string): void;
    // New prop to control whether the component should show loading state
    showLoading?: boolean;
}

const GildandiSkraningAdilar: React.FC<IProps> = ({
    customerId,
    adilarTableData,
    riskScore,
    setEditContactModal_isOpen,
    setEditContactModal_contacts,
    setForeignPepModal_isOpen,
    setForeignPepModal_data,
    setPepModal_isOpen,
    setPepModal_data,
    setToastShow,
    setToastType,
    setToastMessage,
    showLoading = false
}) => {
    const [disableSendNotificationButton, setDisableSendNotificationButton] = useState(!riskScore || riskScore.answered);

    // Determine if we should only show contact information (when there's only one entry and it's a contact person)
    const contactOnly = adilarTableData.length === 1 && adilarTableData[0].hlutverk === "Tengiliður";

    /**
     * Fetches contact information for a given customer ID
     * Makes an API call to retrieve KYC (Know Your Customer) contacts
     * Displays error toast if the fetch fails
     */
    const fetchContacts = async (customerId: number) => {
        try {
            const response = await fetch(`/DueDiligence/GetKycContacts/${customerId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                setToastType("error");
                setToastMessage("Ekki tókst að sækja tengiliði");
                setToastShow(true);
            }

            const data = await response.json();
            return data;
        } catch (err) {
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage("Ekki tókst að sækja tengiliði");
            setToastShow(true);
        }
    }
    return <DisplayBox
        title={!contactOnly ? "Gildandi skráning - aðilar" : "Tengiliður"}
        id="gildandi-skraning-adilar">
        {/* 
            If showLoading prop is true, display loading spinner instead of table content
            This allows the parent component to control the loading state while keeping
            the DisplayBox wrapper and title visible
        */}
        {showLoading ? (
            <Loading />
        ) : (
            <>
                <div className="adilar-table">
                    <Table
                        tableSize="lg"
                        showEmptyTableText
                        data={adilarTableData}
                        columns={[{
                            title: !contactOnly ? "Hlutverk" : "",
                            renderCell: ({ hlutverk }) => !contactOnly ? hlutverk : "",
                            textAlign: 'left',
                        }, {
                            title: "Nafn",
                            renderCell: ({ nafnadila }) => nafnadila,
                            textAlign: 'left',
                        }, {
                            title: "Kennitala",
                            // Display kennitala with dash formatting, or date of birth if kennitala is not available
                            renderCell: ({ kennitala, dateOfBirth }) => kennitala ? insertDash(kennitala) : dateOfBirth ? dateOfBirth : "-",
                            textAlign: 'left',
                        }, {
                            title: AthaettusomRikiTooltipColumnHeader("Ríkisfang"),
                            // Display nationality with warning icon if the country is on FATF (Financial Action Task Force) risk list
                            renderCell: ({ rikisfang, nationalityFatfCountry }) => RenderNationalityWithTooltip(rikisfang, nationalityFatfCountry),
                            textAlign: 'left'
                        }, {
                            title: AthaettusomRikiTooltipColumnHeader("Búseta"),
                            // Display country of residence with warning icon if the country is on FATF risk list
                            renderCell: ({ busetuland, busetulandFatfCountry }: IAdilarTableData) => RenderNationalityWithTooltip(busetuland, busetulandFatfCountry),
                            textAlign: 'left',
                        }, {
                            title: "Framvísun skilríkja",
                            // Display ID verification status: shield icon if verified, "Request sent" if pending, empty if not requested
                            renderCell: ({ framvisunskilr }: IAdilarTableData) => {
                                if (framvisunskilr !== "Beiðni send" && framvisunskilr !== undefined && framvisunskilr !== "" && framvisunskilr !== null) {
                                    const textForTooltip = <span>Auðkenni staðfest þann: <br />{formatDateToIcelandic(framvisunskilr, true)}.</span>;
                                    return <Tooltip noUnderline text={<FontAwesomeIcon className="icon-yes" icon={faShieldCheck} />} tooltip={textForTooltip} />
                                } else if (framvisunskilr === "Beiðni send") {
                                    return 'Beiðni send'
                                } else return ""
                            },
                            textAlign: 'left',
                        }, {
                            title: StjornmalalegTengslPEPTooltipColumnHeader,
                            // Display PEP (Politically Exposed Person) status with option to view detailed information
                            renderCell: ({ stjornmalalegtStatus, stjornmalalegtData }) => stjornmalalegtStatus !== undefined &&
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: '3px', width: 'fit-content' }}>
                                    <Label cursorType="default" labelType={stjornmalalegtStatus ? "yes" : "no"} size="lg" text={stjornmalalegtStatus ? "Já" : "Nei"} />
                                    {stjornmalalegtStatus &&
                                        <Button
                                            size="sm"
                                            buttonType="secondary"
                                            onClick={() => {
                                                // Sort and display domestic PEP data in modal
                                                setPepModal_data(sortEntries(stjornmalalegtData ? [stjornmalalegtData] : null));
                                                setPepModal_isOpen(true);
                                            }}
                                        >
                                            Sækja
                                        </Button>
                                    }
                                </div>,
                            textAlign: 'left',
                        }, {
                            title: AlthjodlegirValistarTooltipColumnHeader,
                            // Display international watchlist status with option to view detailed matches
                            renderCell: ({ althjodlegirvalistarStatus, althjodlegirvalistarData }) => althjodlegirvalistarStatus !== undefined &&
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: '3px', width: 'fit-content' }}>
                                    <Label cursorType="default" labelType={althjodlegirvalistarStatus ? "yes" : "no"} size="lg" text={althjodlegirvalistarStatus ? "Já" : "Nei"} />
                                    {althjodlegirvalistarStatus &&
                                        <Button
                                            size="sm"
                                            buttonType="secondary"
                                            onClick={() => {
                                                // Display foreign PEP/watchlist matches in modal
                                                setForeignPepModal_data(althjodlegirvalistarData?.mappedReport?.match);
                                                setForeignPepModal_isOpen(true);
                                            }}
                                        >
                                            Sækja
                                        </Button>
                                    }
                                </div>,
                            textAlign: 'left',
                        }
                        ]} />

                </div>
                <div style={{ padding: '3px', width: 'fit-content', marginTop: '20px' }}>
                    {/* 
                        Button to send or resend risk assessment survey
                        Disabled if risk score is missing or already answered
                        Fetches contacts and opens modal for sending notification
                    */}
                    <Button size="lg" disabled={disableSendNotificationButton} onClick={async () => {
                        // Disable button during the fetch operation to prevent double-clicks
                        setDisableSendNotificationButton(true);
                        if (customerId) {
                            // Fetch customer contacts and prepare them for the edit modal
                            await fetchContacts(customerId).then(contacts => {
                                setEditContactModal_contacts(contacts);
                            });
                            setEditContactModal_isOpen(true);
                        } else {
                            // Show error if customer ID is not available
                            setToastType("error");
                            setToastMessage("Ekki tókst að sækja tengiliði");
                            setToastShow(true);
                        }
                        // Re-enable button after operation completes
                        setDisableSendNotificationButton(false);
                    }}>{!riskScore?.dateSent ? 'Senda könnun' : 'Senda ítrekun'}</Button>
                    {/* Display the date when the survey was last sent, if available */}
                    {riskScore?.dateSent && <span style={{ marginLeft: '20px' }} className="paragraph--bold">Könnun send {getTableDate(riskScore.dateSent, 'DD/MM/YYYY', '.')}</span>}
                </div>
            </>
        )}
    </DisplayBox>
}

export default GildandiSkraningAdilar;